import React, { useCallback } from 'react';
import { Form } from 'react-final-form';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages, FormattedMessage } from 'react-intl';

import { SelectField } from '../../components/FinalForm/SelectField';
import { SubmitButtonV3 } from '../../components/FinalForm/SubmitButtonV3';
import { TextAreaField } from '../../components/FinalForm/TextAreaField';
import { REASONS_OPTIONS } from '../../i18n/ban';
import { MessageContent } from '../Content/MessageContent/MessageContent';
import type { DeleteTopicsArgs2 } from '../thread-utils';

export const placeholderMessages = defineMessages({
  reason: { defaultMessage: 'Raison', id: 'iV4wrT' },
  description: { defaultMessage: 'Motif description', id: '7rjg5+' },
});

const FORM_SUBSCRIPTION = {};
export type AdminRemoveContentPropTypes = {
  messages: {
    title?: MessageDescriptor;
    keep?: MessageDescriptor;
    remove?: MessageDescriptor;
  };
  content: string;
  status: string;
  remove: (values: DeleteTopicsArgs2) => void;
  cancel?: () => void;
  warning?: React.ReactNode;
  actions?: React.ReactNode;
};

export const AdminRemoveContent: React.FC<AdminRemoveContentPropTypes> = ({
  content,
  warning,
  cancel,
  status,
  messages,
  ...props
}) => {
  const renderAction = useCallback(() => {
    const actions = props?.actions ?? (
      <div className="flex flex-wrap-reverse my-2 gap-2">
        <SubmitButtonV3 variant="contained" onClick={cancel}>
          <FormattedMessage {...messages.keep} />
        </SubmitButtonV3>
        <SubmitButtonV3 variant="outlined">
          <FormattedMessage {...messages.remove} />
        </SubmitButtonV3>
      </div>
    );

    return (
      <div className="mt-4">
        {warning && <div className="mb-4">{warning}</div>}

        <blockquote className="mt-2 pl-2 border-l-4 border-solid border-gray-lighter">
          <MessageContent className="text-gray-medium" content={content} status={status} />
        </blockquote>
        {actions}
      </div>
    );
  }, [cancel, content, messages.keep, messages.remove, props?.actions, status, warning]);

  const renderForm = useCallback(
    ({ handleSubmit }) => (
      <form className="flex flex-col px-3 py-4 sm:px-8 sm:py-10" onSubmit={handleSubmit}>
        <h1 className="text-primary-base">
          <FormattedMessage {...messages.title} />
        </h1>
        <label htmlFor="reason" className="text-sm text-gray-medium mb-2 mt-4">
          <FormattedMessage {...placeholderMessages.reason} />
        </label>
        <SelectField name="motifId" options={REASONS_OPTIONS} required />
        <label htmlFor="description" className="text-sm text-gray-medium my-2">
          <FormattedMessage {...placeholderMessages.description} />
        </label>
        <TextAreaField name="motif" />
        {renderAction()}
      </form>
    ),
    [messages.title, renderAction],
  );

  return (
    <Form
      onSubmit={props.remove}
      subscription={FORM_SUBSCRIPTION}
      render={renderForm}
      initialValues={{ motifId: 0 }}
    />
  );
};
